/**
 * The notification drawer (docs/03 §7 Tier 2; punch item 3.3).
 *
 * EVERY ROW IN THE DRAWER IS ADDRESSED TO ME PERSONALLY. That single rule is
 * what makes the drawer a different surface from the two that already exist
 * beside it, rather than a third view of the same list:
 *
 *   * `/work` and its lanes answer "what is due across the properties I hold"
 *   * the My Work chip (1.6) is the same question, counted
 *   * the drawer answers "what has somebody asked of ME, and what have I let
 *     slip" — every row names me specifically, not a building I happen to hold
 *
 * A lease expiring on a property I manage that nobody assigned me is not a
 * notification; it is a lane. An overdue item I actually own is.
 *
 * The four kinds are docs/03 §7's four words, and there is no fifth.
 */

import { z } from 'zod';

/**
 * `mention` and `ask` come from the same row (`note_mentions`) and are told
 * apart by the note's intent, so a message can never be both — an ask that also
 * counted as a mention would put one event in the drawer twice and make the
 * unread count disagree with the list it sits above.
 */
export const NOTIFICATION_KINDS = ['ask', 'mention', 'transfer', 'overdue'] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export interface NotificationRow {
  /**
   * Stable across refetches and unique within a response: `<kind>:<id>`. Used
   * as the React key and by the tests, never sent back to the server — there is
   * nothing to send it to, because notifications are derived and have no rows
   * of their own to update.
   */
  readonly key: string;
  readonly kind: NotificationKind;
  /** The one-line headline: the question, the item's title, the note's opening. */
  readonly title: string;
  /**
   * Who did this to me. Null on `overdue` alone, which nobody did — a date
   * passed. The UI says "Nobody did this; it came due" by omitting the name
   * rather than by inventing an actor, per ADR-39's rule that a name is a name.
   */
  readonly actorName: string | null;
  /** The property this hangs off, when there is exactly one and I can see it. */
  readonly propertyName: string | null;
  /**
   * When the event happened, and therefore whether it is unread. For a mention,
   * an ask and a transfer this is the moment somebody acted. For an `overdue`
   * item nobody acted at all, so it is the moment the item BECAME overdue —
   * the start of the day after its due date. That is what makes "an item went
   * overdue while I was away" light the dot.
   */
  readonly occurredAt: string;
  /** True when `occurredAt` is later than this person's last drawer open. */
  readonly unread: boolean;
  /**
   * Where to go. Null when the record has no page of its own — a lease reads
   * inside its tenancy, a contact inside its vendor — in which case the row is
   * text rather than a link that guesses. Same rule, and the same table, as the
   * action-item screens use.
   */
  readonly href: string | null;
}

export interface NotificationsResponse {
  readonly rows: readonly NotificationRow[];
  /** Unread across the WHOLE result, counted before the limit. */
  readonly unreadCount: number;
  /** True when `rows` was cut to the limit — the drawer says so. */
  readonly truncated: boolean;
  /** Null until this person has opened the drawer once. */
  readonly seenAt: string | null;
}

/**
 * How many rows the drawer asks for. Fifty is well past what anybody scrolls,
 * and the two kinds that would ever reach it — unanswered asks and overdue
 * items — are both reachable in full on their own screens, so truncation costs
 * a reader nothing they cannot recover.
 */
export const NOTIFICATION_LIMIT = 50;

/**
 * How long an INFORMATIONAL notification stays in the drawer.
 *
 * Mentions and transfers age out at thirty days: they told you something, and a
 * five-month-old "Rob mentioned you" is clutter that pushes live work off the
 * list. Asks and overdue items NEVER age out, because they are not
 * informational — they are outstanding, and hiding an unanswered question
 * because it got old is exactly backwards. That asymmetry is the whole reason
 * `ask` is its own kind rather than a mention with a flag.
 */
export const NOTIFICATION_WINDOW_DAYS = 30;

export const notificationsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(NOTIFICATION_LIMIT).default(NOTIFICATION_LIMIT),
});
export type NotificationsQuery = z.infer<typeof notificationsQuerySchema>;
